import {
  type FocusEvent,
  type InputHTMLAttributes,
  type KeyboardEvent,
  useCallback,
  useRef,
} from 'react'
import cx from 'classnames'
import { type ControlStatus } from '@/components/inputs/control-status'
import {
  type ControlSize,
  useControlSize,
} from '@/components/inputs/control-size'
import { type NumericInputHookConfig, useNumericInput } from '@/hooks/numeric'
import { useObservableFocus, useFocusTracking } from '@/hooks/focus-hooks'
import styles from './NumericInput.module.css'
import { NEVER, type Observable } from 'rxjs'
import { getCurrencyCode } from '@/utils/money-util'
import { match } from '@/utils/pattern-match-util'

export interface NumericInputProps
  extends
    Omit<
      InputHTMLAttributes<HTMLInputElement>,
      'value' | 'onChange' | 'disabled' | 'size'
    >,
    NumericInputHookConfig {
  isDisabled?: boolean
  currencyCode?: string
  status?: ControlStatus
  /**
   * Defaults to the size of the surrounding `ControlSizeContext`.
   */
  size?: ControlSize
  focus$?: Observable<unknown>
  isNotValid?: boolean
  testId?: string
}

/**
 * A text input for numbers, formatted for the locale.
 */
export function NumericInput({
  className,
  value,
  onChange,
  precision = 0,
  fixedPrecision,
  locale,
  useGrouping,
  onBlur,
  focus$ = NEVER,
  status = 'default',
  size: sizeProp,
  mode = 'live',
  allowNegative,
  currencyCode,
  isNotValid,
  testId,
  isDisabled,
  onKeyDown,
  ...props
}: NumericInputProps) {
  const size = useControlSize(sizeProp)
  const inputRef = useRef<HTMLInputElement | null>(null)
  useObservableFocus(focus$, inputRef)
  const connector = useNumericInput({
    value,
    precision,
    fixedPrecision,
    mode,
    locale,
    useGrouping,
    onChange,
    allowNegative,
  })
  const { commit } = connector
  const blur = useCallback(
    (e: FocusEvent<HTMLInputElement>) => {
      if (mode === 'draft') {
        commit()
      }
      if (onBlur) {
        onBlur(e)
      }
    },
    [onBlur, mode, commit],
  )
  const [focusProps, isFocused] = useFocusTracking({
    onBlur: blur,
    onFocus: props.onFocus,
  })

  const keyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      connector.keyDownPreventNonNumeric(e)
      if (e.key === 'Enter' && mode === 'draft') {
        connector.commit()
      }
      onKeyDown?.(e)
    },
    [connector, mode, onKeyDown],
  )

  return (
    <div
      className={cx(
        styles.input,
        match(size, {
          normal: undefined,
          compact: styles.compact,
          small: styles.small,
        }),
        isDisabled && styles.disabled,
        isFocused && styles.focused,
        isNotValid && styles.isNotValid,
        styles[status],
      )}
    >
      {currencyCode && (
        <div className={styles.currencySymbol}>
          {getCurrencyCode(currencyCode)}
        </div>
      )}
      <input
        className={cx(styles.inner, className)}
        data-test={testId}
        type="text"
        ref={inputRef}
        disabled={isDisabled}
        {...props}
        value={connector.value}
        onChange={(e) => connector.onChange(e.target.value)}
        {...focusProps}
        onKeyDown={keyDown}
      />
    </div>
  )
}
